<template>
    <fragment>
        <form ref="form" class="form" :class="{ 'form--submitting': isSubmitting }" @submit.prevent="submitForm" v-if="!hasBeenSubmitted" autocomplete="off">
            <fragment v-if="isInitializing">
                <div class="form__row">
                    <div class="form__field-wrap">
                        <div class="form__placeholder-input"></div>
                    </div>
                </div>
                <div class="form__row">
                    <div class="form__field-wrap">
                        <div class="form__placeholder-input"></div>
                    </div>
                </div>
                <div class="form__row form__row--policy">
                    <div class="form__field-wrap">
                        <div class="form__placeholder-checkbox"></div>
                    </div>
                </div>
                <div class="form__row form__row--submit">
                    <div class="form__field-wrap">
                        <div class="form__placeholder-button"></div>
                    </div>
                </div>
            </fragment>
            <fragment v-else>
                <fragment v-for="(field, index) in filteredCFields" :key="index">

                    <!-- Standard Field (type: "Header") -->
                    <template v-if="field.type === 'header'">
                        <span class="form__headline">{{field.header}}</span>
                    </template>

                    <!-- Standard Field (type: "Text") -->
                    <template v-else-if="field.type === 'fullname' || field.type === 'firstname' || field.type === 'lastname' || field.type === 'customer_account'">
                        <div class="form__row">
                            <label v-if="field.header" :for="getUniqueId(field.type)" class="form__label">{{getFieldHeader(field)}}</label>
                            <input :id="getUniqueId(field.type)" :name="field.type" type="text" class="form__input" :placeholder="getFieldTitle(field, true)" :title="getFieldTitle(field)" :aria-required="field.required" :required="field.required" />
                        </div>
                    </template>

                    <!-- Standard Field (type: "Email") -->
                    <template v-else-if="field.type === 'email'">
                        <div class="form__row">
                            <label v-if="field.header" :for="getUniqueId(field.type)" class="form__label">{{getFieldHeader(field)}}</label>
                            <input v-model="email" :id="getUniqueId(field.type)" :name="field.type" type="email" class="form__input" :placeholder="getFieldTitle(field, true)" :title="getFieldTitle(field)" :aria-required="field.required" :required="field.required" />
                        </div>
                    </template>

                    <!-- Standard Field (type: "Phone") -->
                    <template v-else-if="field.type === 'phone'">
                        <div class="form__row">
                            <label v-if="field.header" :for="getUniqueId(field.type)" class="form__label">{{getFieldHeader(field)}}</label>
                            <input :id="getUniqueId(field.type)" :name="field.type" type="tel" class="form__input" :placeholder="getFieldTitle(field, true)" :title="getFieldTitle(field)" :aria-required="field.required" :required="field.required" />
                        </div>
                    </template>

                    <!-- Custom Field (type: "Text Input") -->
                    <template v-else-if="field.type === 'text'">
                        <div class="form__row">
                            <label v-if="field.header" :for="getUniqueId(field.id)" class="form__label">{{getFieldHeader(field)}}</label>
                            <input :id="getUniqueId(field.id)" :name="field.id" type="text" class="form__input" data-custom-field :placeholder="getFieldTitle(field, true)" :title="getFieldTitle(field)" :aria-required="field.required" :required="field.required" />
                        </div>
                    </template>

                    <!-- Custom Field (type: "Text Area") -->
                    <template v-else-if="field.type === 'textarea'">
                        <div class="form__row">
                            <label v-if="field.header" :for="getUniqueId(field.id)" class="form__label">{{getFieldHeader(field)}}</label>
                            <textarea :id="getUniqueId(field.id)" :name="field.id" class="form__textarea" data-custom-field :placeholder="getFieldTitle(field, true)" :title="getFieldTitle(field)" :aria-required="field.required" :required="field.required"></textarea>
                        </div>
                    </template>

                    <!-- Custom Field (type: "Dropdown") -->
                    <template v-else-if="field.type === 'dropdown'">
                        <div class="form__row" v-if="field.options">
                            <label v-if="field.header" :for="getUniqueId(field.id)" class="form__label">{{getFieldHeader(field)}}</label>
                            <div class="form__select-wrap">
                                <select :id="getUniqueId(field.id)" :name="field.id" class="form__select" data-custom-field :title="getFieldTitle(field)" :aria-required="field.required" :required="field.required">
                                    <option value="" :selected="!field.options.some(o => o.isDefault == '1')">{{getFieldHeader(field)}}</option>
                                    <option :value="option.value" v-for="(option, index) in field.options" :key="index" :selected="option.isDefault == '1'">{{option.label}}</option>
                                </select>
                            </div>
                        </div>
                    </template>

                    <!-- Custom Field (type: "List Box") -->
                    <template v-else-if="field.type === 'listbox'">
                        <div class="form__row" v-if="field.options">
                            <label v-if="field.header" :for="getUniqueId(field.id)" class="form__label">{{getFieldHeader(field)}}</label>
                            <div class="form__select-wrap form__select-wrap--multiple">
                                <select :id="getUniqueId(field.id)" :name="field.id" class="form__select" data-custom-field :title="getFieldTitle(field)" multiple :aria-required="field.required" :required="field.required">
                                    <option :value="option.value" v-for="(option, index) in field.options" :key="index" :selected="option.isDefault == '1'">{{option.label}}</option>
                                </select>
                            </div>
                        </div>
                    </template>

                    <!-- Custom Field (type: "Radio Button") -->
                    <template v-else-if="field.type === 'radio'">
                        <div class="form__row">
                            <span v-if="field.header" class="form__label">{{getFieldHeader(field)}}</span>
                            <div class="form__checkbox-radio-list">
                                <div class="form__checkbox-radio-wrap" v-for="(option, index) in field.options" :key="index">
                                    <input :id="getUniqueId(field.id, option.id)" class="form__radio" data-custom-field type="radio" :name="field.id" :value="option.value" :checked="option.isDefault == '1'" :aria-required="field.required" :required="field.required" />
                                    <label :for="getUniqueId(field.id, option.id)" class="form__checkbox-radio-label">{{option.label}}</label>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- Custom Field (type: "Checkbox") -->
                    <template v-else-if="field.type === 'checkbox'">
                        <div class="form__row" v-if="field.options">
                            <fragment v-if="field.options.length > 1">
                                <span v-if="field.header" class="form__label">{{getFieldHeader(field)}}</span>
                                <div class="form__checkbox-radio-list">
                                    <div class="form__checkbox-radio-wrap" v-for="(option, index) in field.options" :key="index">
                                        <input :id="getUniqueId(field.id, option.id)" class="form__checkbox" data-custom-field type="checkbox" :name="field.id" :value="option.value" :checked="option.isDefault == '1'" />
                                        <label :for="getUniqueId(field.id, option.id)" class="form__checkbox-radio-label">{{option.label}}</label>
                                    </div>
                                </div>
                            </fragment>
                            <fragment v-else>
                                <span v-if="field.header" class="form__label">{{getFieldHeader(field)}}</span>
                                <div class="form__checkbox-radio-wrap">
                                    <input :id="getUniqueId(field.id, field.options[0].id)" class="form__checkbox" data-custom-field type="checkbox" :name="field.id" :value="field.options[0].value" :checked="field.options[0].isDefault == '1'" :aria-required="field.required" :required="field.required" />
                                    <label :for="getUniqueId(field.id, field.options[0].id)" class="form__checkbox-radio-label">{{field.options[0].label}}</label>
                                </div>
                            </fragment>
                        </div>
                    </template>

                    <!-- Custom Field (type: "Policy") -->
                    <template v-else-if="field.type === 'policy'">
                        <div class="form__row" v-if="field.options">
                            <div class="form__checkbox-radio-wrap form__checkbox-radio-wrap--policy">
                                <span v-if="field.header" class="form__label">{{getFieldHeader(field)}}</span>
                                <div class="form__checkbox-radio-wrap">
                                    <input :id="getUniqueId(field.id, field.options[0].id)" class="form__checkbox" data-custom-field type="checkbox" :name="field.id" :value="field.options[0].value" :checked="field.options[0].isDefault == '1'" :aria-required="field.required" :required="field.required" />
                                    <label :for="getUniqueId(field.id, field.options[0].id)" class="form__checkbox-radio-label">
                                        <span v-html="getPolicyLabel(field)"></span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </template>

                    <!-- Custom Field (type: "Hidden") -->
                    <template v-else-if="field.type === 'hidden'">
                        <input :id="getUniqueId(field.id)" :name="field.id" data-custom-field type="hidden" :value="field.default_text" />
                    </template>

                    <!-- Unsupported types -->
                    <template v-else>
                        <div class="form__row" style="border: 2px dotted red; font-size: 12px;">
                            <p>Field type not supported: <strong>{{field.type}} // {{field.header}}</strong></p>
                        </div>
                    </template>

                </fragment>
                <div class="form__row" v-if="form.cfields">
                    <div class="form__btn-wrap">
                        <button type="submit" class="form__btn btn">
                            <span class="btn__icon">
                                <SvgInline name="arrow"></SvgInline>
                            </span>
                            <span class="btn__text">{{form.button}}</span>
                            <span class="btn__icon">
                                <SvgInline name="arrow"></SvgInline>
                            </span>
                        </button>
                    </div>
                </div>
                <div class="spinner-wrap" :class="{ 'spinner-wrap--absolute': isSubmitting }" :aria-hidden="isSubmitting ? 'false' : 'true'">
                    <div class="spinner"></div>
                </div>
            </fragment>
        </form>
        <div class="form__message" :class="{ 'form__message--error': hasError }" v-if="message">{{message}}</div>
    </fragment>
</template>

<script>
    import apiService from '../../api';
    import SvgInline from '../components/svg-inline.vue';

    export default {
        name: 'ac-form',
        components: {
            SvgInline
        },
        data() {
            return {
                isInitializing: true,
                isSubmitting: false,
                hasBeenSubmitted: false,
                form: [],
                hasError: false,
                message: '',
                email: ''
            };
        },
        created() {
            this.formId = this.$parent.formId;
            this.formKey = this.$parent.formKey;
            this.getForm();
        },
        methods: {
            getForm() {
                const vm = this;

                apiService.ac.getForm(vm.formId)
                    .then(response => {
                        vm.form = response.data.form;
                    })
                    .catch(errors => {
                        const errorResponse = errors.response.data.message ? errors.response.data.message : `Error (status code ${errors.response.status})`;

                        /* temp for prototyping */
                        if (location.hostname.includes('localhost') && location.port == 8080) {
                            const tempResponse = {"form":{"name":"Gotcha test (all fields) - DO NOT DELETE","action":"","actiondata":{"actions":[{"type":"add-a-tag","email":null,"list":null,"tag":"tag test 1"},{"type":"add-a-tag","email":null,"list":null,"tag":"tag test 2"}]},"submit":"show-thank-you","submitdata":{"url":""},"url":"","layout":"inline-form","title":"","body":"","button":"Submit","thanks":"Thanks for signing up!","style":{"background":"FFFFFF","dark":true,"fontcolor":"000000","layout":"normal","fontFamily":"\"IBM Plex Sans\", Helvetica, sans-serif","border":{"width":0,"style":"solid","color":"B0B0B0","radius":0},"width":500,"ac_branding":false,"button":{"padding":10,"background":"333333","fontcolor":"FFFFFF","border":{"width":0,"style":"solid","color":"333333","radius":4}}},"options":{"blanks_overwrite":true,"confaction":"show-message","sendoptin":false,"optin_id":5,"optin_created":true,"confform":"4"},"cfields":[{"type":"header","header":"Form header","class":null,"html":null,"default_text":null,"id":null,"required":false,"options":null},{"type":"html","header":null,"class":null,"html":"aeraer ear aer earoiaekreaik ioraej ioaerae raer earae raerea","default_text":null,"id":null,"required":false,"options":null},{"type":"hidden","header":"divider","class":null,"html":null,"default_text":"","id":"3","required":false,"options":[]},{"type":"fullname","header":"Full Name","class":null,"html":null,"default_text":"Placeholder for Full Name","id":null,"required":false,"options":null},{"type":"email","header":"Email","class":null,"html":null,"default_text":"Placeholder for Email","id":null,"required":true,"options":null},{"type":"hidden","header":"divider1","class":null,"html":null,"default_text":"","id":"14","required":false,"options":[]},{"type":"phone","header":"Phone","class":null,"html":null,"default_text":"Placeholder for Phone","id":null,"required":false,"options":null},{"type":"customer_account","header":"Account","class":null,"html":null,"default_text":"Placeholder for Account","id":null,"required":false,"options":null},{"type":"hidden","header":"divider2","class":null,"html":null,"default_text":"","id":"15","required":false,"options":[]},{"type":"unsubscribe","header":"Choose whether to subscribe or unsubscribe","class":null,"html":null,"default_text":null,"id":null,"required":true,"options":null},{"type":"image","header":null,"class":null,"html":null,"default_text":null,"id":null,"required":false,"options":null},{"type":"hidden","header":"divider3","class":null,"html":null,"default_text":"","id":"16","required":false,"options":[]},{"type":"text","header":"Custom Field (type: Text Input)","class":null,"html":null,"default_text":"Placeholder for Custom Field (type: Text Input)","id":"5","required":false,"options":[]},{"type":"hidden","header":"divider4","class":null,"html":null,"default_text":"","id":"17","required":false,"options":[]},{"type":"textarea","header":"Custom Field (type: Text Area)","class":null,"html":null,"default_text":"Placeholder for Custom Field (type: Text Area)","id":"4","required":false,"options":[]},{"type":"hidden","header":"divider5","class":null,"html":null,"default_text":"","id":"18","required":false,"options":[]},{"type":"date","header":"Custom Field (type: Date)","class":null,"html":null,"default_text":"","id":"6","required":false,"options":[]},{"type":"datetime","header":"Custom Field (type: Date Time)","class":null,"html":null,"default_text":"","id":"7","required":false,"options":[]},{"type":"hidden","header":"divider6","class":null,"html":null,"default_text":"","id":"19","required":false,"options":[]},{"type":"dropdown","header":"Custom Field (type: Dropdown)","class":null,"html":null,"default_text":"","id":"8","required":true,"options":[{"field":"1","orderId":1,"value":"Custom Field (type: Dropdown) - Option 1","label":"Custom Field (type: Dropdown) - Option 1","isDefault":0,"cdate":"2022-01-13T08:41:59-06:00","udate":"2022-01-20T07:34:09-06:00","id":"1"},{"field":"2","orderId":2,"value":"Custom Field (type: Dropdown) - Option 2","label":"Custom Field (type: Dropdown) - Option 2","isDefault":0,"cdate":"2022-01-13T08:41:59-06:00","udate":"2022-07-07T06:55:04-05:00","id":"2"},{"field":"3","orderId":3,"value":"Custom Field (type: Dropdown) - Option 3","label":"Custom Field (type: Dropdown) - Option 3","isDefault":0,"cdate":"2022-01-13T08:41:59-06:00","udate":"2022-01-19T14:11:48-06:00","id":"3"}]},{"type":"listbox","header":"Custom Field (type: List Box)","class":null,"html":null,"default_text":"","id":"9","required":false,"options":[{"field":"4","orderId":1,"value":"Custom Field (type: List Box) - Option 1","label":"Custom Field (type: List Box) - Option 1","isDefault":0,"cdate":"2022-01-13T08:42:46-06:00","udate":"2022-07-07T07:16:52-05:00","id":"4"},{"field":"5","orderId":2,"value":"Custom Field (type: List Box) - Option 2","label":"Custom Field (type: List Box) - Option 2","isDefault":0,"cdate":"2022-01-13T08:42:46-06:00","udate":"2022-01-20T07:10:45-06:00","id":"5"},{"field":"6","orderId":3,"value":"Custom Field (type: List Box) - Option 3","label":"Custom Field (type: List Box) - Option 3","isDefault":0,"cdate":"2022-01-13T08:42:46-06:00","udate":"2022-07-07T07:16:52-05:00","id":"6"}]},{"type":"hidden","header":"divider7","class":null,"html":null,"default_text":"","id":"20","required":false,"options":[]},{"type":"radio","header":"Custom Field (type: Radio Button)","class":null,"html":null,"default_text":"","id":"10","required":false,"options":[{"field":"7","orderId":1,"value":"Custom Field (type: Radio Button) - Option 1","label":"Custom Field (type: Radio Button) - Option 1","isDefault":0,"cdate":"2022-01-13T08:42:59-06:00","udate":"2022-07-07T07:17:22-05:00","id":"7"},{"field":"8","orderId":2,"value":"Custom Field (type: Radio Button) - Option 2","label":"Custom Field (type: Radio Button) - Option 2","isDefault":0,"cdate":"2022-01-13T08:42:59-06:00","udate":"2022-01-20T06:47:01-06:00","id":"8"}]},{"type":"checkbox","header":"Custom Field (type: Checkbox - Multiple)","class":null,"html":null,"default_text":null,"id":"11","required":false,"options":[{"field":"11","orderId":1,"value":"Custom Field (type: Checkbox) - Option 1","label":"Custom Field (type: Checkbox) - Option 1","isDefault":0,"cdate":"2022-01-13T08:43:13-06:00","udate":"2022-01-20T03:43:12-06:00","id":"10"},{"field":"11","orderId":2,"value":"Custom Field (type: Checkbox) - Option 2","label":"Custom Field (type: Checkbox) - Option 2","isDefault":0,"cdate":"2022-01-13T08:43:13-06:00","udate":"2022-01-20T03:43:12-06:00","id":"11"},{"field":"11","orderId":3,"value":"Custom Field (type: Checkbox) - Option 3","label":"Custom Field (type: Checkbox) - Option 3","isDefault":0,"cdate":"2022-01-13T08:43:13-06:00","udate":"2022-01-19T14:13:12-06:00","id":"12"},{"field":"11","orderId":4,"value":"Custom Field (type: Checkbox) - Option 4","label":"Custom Field (type: Checkbox) - Option 4","isDefault":0,"cdate":"2022-01-19T14:15:30-06:00","udate":"2022-01-19T14:15:30-06:00","id":"18"},{"field":"11","orderId":5,"value":"Custom Field (type: Checkbox) - Option 5","label":"Custom Field (type: Checkbox) - Option 5","isDefault":0,"cdate":"2022-01-19T14:15:39-06:00","udate":"2022-01-19T14:15:39-06:00","id":"19"}]},{"type":"checkbox","header":"Custom Field (type: Checkbox - Single)","class":null,"html":null,"default_text":"","id":"23","required":false,"options":[{"field":"20","orderId":1,"value":"Custom Field (type: Checkbox - Option 1)","label":"Custom Field (type: Checkbox - Option 1)","isDefault":0,"cdate":"2022-01-20T02:59:59-06:00","udate":"2022-01-20T07:29:04-06:00","id":"20"}]},{"type":"hidden","header":"divider8","class":null,"html":null,"default_text":"","id":"21","required":false,"options":[]},{"type":"policy","header":"Policy Accept","class":null,"html":null,"default_text":"","id":"13","required":true,"options":[{"field":"13","orderId":1,"value":"Lorem ipsum dolor sit amet, consectetur adipiscing elit. Read our {link:Privacy policy}","label":"Lorem ipsum dolor sit amet, consectetur adipiscing elit. Read our <a href='/privatlivspolitik' target='_blank'>Privacy policy</a>","isDefault":0,"cdate":"2022-01-13T08:53:36-06:00","udate":"2022-07-08T02:27:27-05:00","id":"13"}]},{"type":"hidden","header":"Custom Field (type: Hidden Field)","class":null,"html":null,"default_text":"Test","id":"12","required":false,"options":[]}],"parentformid":"0","userid":"1","addressid":"1","cdate":"2021-11-17T15:15:33+01:00","udate":"2023-04-03T14:09:23+02:00","entries":"17","aid":"0","links":{"notes":null,"accountCustomFieldData":null,"accountContacts":null,"emailActivities":null,"contactEmails":null,"owner":null},"id":"3","address":"1"}};
                            vm.form = tempResponse.form;
                        } else {
                            vm.hasError = true;
                            vm.message = errorResponse;
                        }

                    })
                    .then(function () {
                        vm.isInitializing = false;
                    });
            },
            submitForm() {
                const vm = this,
                    contactObj = vm.mappedContactObj();

                vm.isSubmitting = true;
                vm.hasError = false;
                vm.message = '';

                apiService.ac.addContact(contactObj, vm.form.actiondata)
                    .then(response => {

                        // /* Tracking */
                        window.dataLayer = window.dataLayer || [];
                        window.dataLayer.push({
                            'event': 'form_submission',
                            'formName': vm.form.name,
                            'userEmail': vm.email
                        });

                        if (vm.form.submit === 'open-url' && vm.form.submitdata.url) {
                            window.location.href = vm.form.submitdata.url;
                        } else {
                            vm.message = vm.form.thanks;
                            vm.hasBeenSubmitted = true;
                        }
                    })
                    .catch(errors => {
                        const errorResponse = errors.response.data.message ? errors.response.data.message : `Error (status code ${errors.response.status})`;
                        vm.hasError = true;
                        vm.message = errorResponse;
                        vm.hasBeenSubmitted = true;
                    })
                    .then(function () {
                        vm.isSubmitting = false;
                    });
            },
            mappedContactObj() {
                const form = this.$refs.form,
                    formElements = form.querySelectorAll('input, textarea, select'),
                    formData = {
                        fieldValues: []
                    };

                if (formElements) {
                    formElements.forEach(el => {

                        if (el.hasAttribute('data-custom-field')) {

                            if (el.type && el.type === 'radio') {

                                if (el.checked) {
                                    formData.fieldValues.push({ field: el.name, value: el.value });
                                }

                            } else if (el.type && el.type === 'checkbox') {

                                if (el.checked && !formData.fieldValues.some(e => e.field === el.name)) {
                                    const possibleSiblings = form.querySelectorAll(`[name="${el.name}"]:checked`);
                                    const selectedCheckboxes = Array.from(possibleSiblings).map(({ value }) => value);
                                    const values = selectedCheckboxes.join('||');
                                    formData.fieldValues.push({ field: el.name, value: `||${values}||` });
                                }

                            } else if (el.tagName === 'SELECT' && el.hasAttribute('multiple')) {

                                if (el.selectedIndex !== -1) {
                                    const selectedOptions = Array.from(el.selectedOptions).map(({ value }) => value);
                                    const values = selectedOptions.join('||');
                                    formData.fieldValues.push({ field: el.name, value: `||${values}||` });
                                }

                            } else {

                                if (el.value) {
                                    formData.fieldValues.push({ field: el.name, value: el.value });
                                }
                            }

                        } else {

                            if (!el.value) {
                                return;
                            }

                            if (el.name === 'fullname') {
                                const fullName = el.value;
                                let firstName = fullName;
                                let lastName = '';

                                if (fullName.indexOf(' ') >= 0) {
                                    firstName = fullName.substr(0,fullName.indexOf(' '));
                                    lastName = fullName.substr(fullName.indexOf(' ') + 1);
                                }

                                formData['firstname'] = firstName;
                                formData['lastname'] = lastName;

                            } else {
                                formData[el.name] = el.value;
                            }
                        }
                    });
                }

                return formData;
            },
            getUniqueId(value1, value2 = '') {
                let uniqueId = 'form[' + this.formKey + ']-' + value1;
                uniqueId = value2 ? uniqueId + '-' + value2 : uniqueId;
                return uniqueId;
            },
            getFieldHeader(field) {
                let label = field.header;
                if (field.required && label.length)
                {
                    label = label.replace(/\*/g, '') + '*';
                }
                return label;
            },
            getFieldTitle(field, isPlaceholder) {
                let label = field.default_text;
                if (field.required && label.length)
                {
                    label = label.replace(/\*/g, '');

                    if (isPlaceholder && !field.header) {
                        label = label + '*';
                    }
                }
                return label;
            },
            getPolicyLabel(field) {
                let label = field.options[0].label;
                if (field.required && label.length)
                {
                    label = label.replace(/\*/g, '');

                    if (!field.header) {
                        label = label + '*';
                    }
                }
                return label;
            }
        },
        computed: {
            filteredCFields() {
                if (!this.isInitializing) {
                    const allowedTypes = [ 'header', 'fullname', 'firstname', 'lastname', 'customer_account', 'email', 'phone', 'text', 'textarea', 'dropdown', 'listbox', 'radio', 'checkbox', 'policy', 'hidden' ];
                    let filteredObject = this.form.cfields;
                    if (this.form.cfields) {
                        filteredObject = this.form.cfields
                            .filter(cfield => {
                                return (allowedTypes.includes(cfield.type))
                            })
                    }
                    return filteredObject;
                }
            }
        }
    };
</script>